class Popup extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).innerHTML = this._template();

    // refs
    this._panel = this.shadowRoot.querySelector(".panel");
    this._title = this.shadowRoot.querySelector(".title");
    this._body = this.shadowRoot.querySelector(".body");
    this._btnClose = this.shadowRoot.querySelector(".close");
    this._backdrop = this.shadowRoot.querySelector(".backdrop");

    // handlers
    this._handleBackdropClick = () => this.close();
    this._handleCloseClick = () => this.close();
    this._handleKeydown = (e) => {
      if (e.key === "Escape" && this.isOpen) this.close();
    };
    this._handleOpenEvent = (e) => {
      const d = e.detail || {};
      if (d.for !== this.id) return;
      if (typeof d.heading === "string") this.setAttribute("heading", d.heading);
      if (typeof d.styles === "string") this.setAttribute("styles", d.styles);
      if (typeof d.content === "string") this._body.innerHTML = d.content;
      this.open();
    };

    // delegated click handler for elements with [data-popup-open]
    this._handleTriggerClick = (e) => {
      const trigger = e.target.closest("[data-popup-open]");
      if (!trigger) return;
      const targetId = trigger.getAttribute("data-popup-open");
      if (targetId === this.id) this.open();
    };
  }

  static get observedAttributes() {
    return ["heading", "styles"];
  }

  attributeChangedCallback(name, _, val) {
    if (name === "heading") this._setHeading(val || "");
    if (name === "styles") this._applyPanelStyles(val || "");
  }

  connectedCallback() {
    if (this.hasAttribute("heading")) this._setHeading(this.getAttribute("heading") || "");
    if (this.hasAttribute("styles")) this._applyPanelStyles(this.getAttribute("styles") || "");

    this._backdrop.addEventListener("click", this._handleBackdropClick);
    this._btnClose.addEventListener("click", this._handleCloseClick);
    document.addEventListener("keydown", this._handleKeydown);
    document.addEventListener("popup:open", this._handleOpenEvent);
    document.addEventListener("click", this._handleTriggerClick);
  }

  disconnectedCallback() {
    this._backdrop.removeEventListener("click", this._handleBackdropClick);
    this._btnClose.removeEventListener("click", this._handleCloseClick);
    document.removeEventListener("keydown", this._handleKeydown);
    document.removeEventListener("popup:open", this._handleOpenEvent);
    document.removeEventListener("click", this._handleTriggerClick);
  }

  get isOpen() {
    return this.hasAttribute("open");
  }

  open() {
    this.setAttribute("open", "");
    document.body.style.overflow = "hidden";
  }

  close() {
    this.removeAttribute("open");
    document.body.style.overflow = "";
  }

  _setHeading(text) {
    this._title.textContent = text;
  }

  _applyPanelStyles(cssText) {
    this._panel.style.cssText = cssText;
  }

  _template() {
    return `
      <style>
        *,*::before,*::after { box-sizing: border-box; }

        :host {
          position:fixed;
          inset:0;
          z-index:999999;
          display:flex;
          align-items:center;
          justify-content:center;
          opacity:0;
          visibility:hidden;
          transition:opacity 0.3s ease, visibility 0.3s ease;
        }

        :host([open]) {
          opacity:1;
          visibility:visible;
        }

        .backdrop {
          position:absolute;
          inset:0;
          background:rgba(0,0,0,0.3);
          transition:opacity 0.3s ease;
        }

        .panel {
          position:relative;
          background:#fff;
          border-radius:20px;
          width:90%;
          max-width:800px;
          height:90vh;
          display:flex;
          flex-direction:column;
          opacity:0;
          visibility:hidden;
          transform:translate(-50%,-50%);
          top:50%;
          left:50%;
          position:absolute;
          transition:opacity 0.3s ease, visibility 0.3s ease;
        }

        :host([open]) .panel {
          opacity:1;
          visibility:visible;
        }

        .header {
          display:flex;
          justify-content:space-between;
          align-items:center;
          padding:20px 20px 10px 20px;
          flex:0 0 auto;
        }

        .title { margin:0; font-size:24px; font-family:"Kanit-Regular"; }
        .close { border:0; background:none;cursor:pointer; }

        .body {
          padding:10px 20px 40px 20px;
          flex:1 1 auto;
          min-height:0;
          overflow-y:auto;
          -webkit-overflow-scrolling:touch;
        }
      </style>
      <div class="backdrop"></div>
      <div class="panel">
        <div class="header">
          <h2 class="title"></h2>
          <button class="close" type="button" aria-label="Close" title="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.5859 10L0.792969 2.20706L2.20718 0.792847L10.0001 8.5857L17.793 0.792847L19.2072 2.20706L11.4143 10L19.2072 17.7928L17.793 19.2071L10.0001 11.4142L2.20718 19.2071L0.792969 17.7928L8.5859 10Z" fill="black"/> </svg>
          </button>
        </div>
        <div class="body"><slot></slot></div>
      </div>
    `;
  }
}

customElements.define("x-popup", Popup);
